import { useEffect, useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { formatReference, formatShortReference } from '@skjv/common';
import type { Bookmark } from '@skjv/common';
import { StarIcon } from '@components/icons';
import { BookmarkMoveConfirm } from './bookmark-move-confirm';

/**
 * Bookmark creator modal - allows naming a new bookmark or moving a favorite.
 */
export const BookmarkCreator = observer(function BookmarkCreator() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );
  const [name, setName] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [confirmingMove, setConfirmingMove] = useState<Bookmark | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isVisible = bibleState.bookmarkCreatorVisible;
  const location = bibleState.interactionLocation;

  // Get top 7 favorite bookmarks
  const favoriteBookmarks = bibleState.sortedBookmarks.filter((b) => b.isFavorite).slice(0, 7);

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      setName('');
      setIsFavorite(false);
      setConfirmingMove(null);
      const timer = setTimeout(() => {
        setAnimationState('visible');
        inputRef.current?.focus();
      }, 250);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Handle save new bookmark
  const handleSave = useCallback(() => {
    if (!name.trim()) {
      bibleState.showToast('Please enter a bookmark name', 'warning');
      return;
    }
    bibleState.addBookmark(name.trim(), isFavorite);
    bibleState.hideBookmarkCreator();
  }, [name, isFavorite]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setConfirmingMove(null);
    bibleState.hideBookmarkCreator();
  }, []);

  // Handle key press
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        if (confirmingMove) {
          setConfirmingMove(null);
        } else {
          handleCancel();
        }
      }
    },
    [handleSave, handleCancel, confirmingMove],
  );

  // Handle clicking a favorite to move it
  const handleFavoriteClick = (bookmark: Bookmark) => {
    setConfirmingMove(bookmark);
  };

  // Handle confirming the move
  const handleConfirmMove = () => {
    if (!confirmingMove || !location) return;

    // Move the bookmark to the new location
    bibleState.moveBookmark(confirmingMove.name, {
      testamentIndex: location.testamentIndex,
      bookIndex: location.bookIndex,
      chapterIndex: location.chapterIndex,
      verseIndex: location.verseIndex,
    });

    bibleState.hideBookmarkCreator();
  };

  // Handle canceling the move confirmation
  const handleCancelMove = () => {
    setConfirmingMove(null);
  };

  if (!shouldRender || !location) return null;

  const reference = formatReference(location);

  // Show confirmation dialog for moving a favorite
  if (confirmingMove) {
    return (
      <BookmarkMoveConfirm
        bookmark={confirmingMove}
        targetReference={reference}
        animationState={animationState}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
      />
    );
  }

  return (
    <>
      {/* Overlay */}
      <div className={`bookmark-creator-overlay ${animationState}`} onClick={handleCancel} />

      {/* Modal */}
      <div
        className={`bookmark-creator ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
      >
        <h3 className="mb-1 text-xl font-semibold text-stone-800 dark:text-stone-200">
          New Bookmark
        </h3>
        <p className="mb-4 text-stone-600 dark:text-stone-400">{reference}</p>

        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Bookmark name..."
          className="bookmark-input mb-4"
        />

        <label className="mb-4 flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={isFavorite}
            onChange={(e) => setIsFavorite(e.target.checked)}
            className="h-5 w-5 rounded border-stone-300 text-cyan-600 focus:ring-cyan-500"
          />
          <span className="text-stone-700 dark:text-stone-300">Add to favorites</span>
        </label>

        <div className="mb-4 flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 rounded-xl bg-stone-200/50 py-3 text-lg font-medium text-stone-600 transition-all hover:bg-stone-300/70 dark:bg-stone-700/30 dark:text-stone-400 dark:hover:bg-stone-600/40"
          >
            Cancel
          </button>
          <button onClick={handleSave} className="bookmark-save-btn flex-1">
            Save
          </button>
        </div>

        {/* Favorite bookmarks to move */}
        {favoriteBookmarks.length > 0 && (
          <div className="border-t border-stone-200 pt-4 dark:border-stone-700">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400">
              Or move a favorite here
            </p>
            <div className="max-h-48 space-y-1.5 overflow-y-auto">
              {favoriteBookmarks.map((bookmark) => (
                <button
                  key={bookmark.name}
                  onClick={() => handleFavoriteClick(bookmark)}
                  className="flex w-full items-center gap-2 rounded-lg border border-amber-200/50 bg-amber-50/50 px-3 py-2 text-left transition-colors hover:bg-amber-100 dark:border-amber-700/30 dark:bg-amber-900/20 dark:hover:bg-amber-900/40"
                >
                  <StarIcon className="h-4 w-4 flex-shrink-0 text-amber-500" filled />
                  <span className="flex-shrink-0 text-xs text-stone-500 dark:text-stone-400">
                    {formatShortReference(bookmark)}
                  </span>
                  <span className="truncate text-sm font-medium text-stone-700 dark:text-stone-300">
                    {bookmark.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default BookmarkCreator;
